// src/prototype/data/execute/vendorEvaluation.ts
// ============================================================
// VENDOR EVALUATION (Oct 21, v3) — output schema of the Vendor Evaluation Agent, the default
// rubric, and the client-side re-ranking used when the client adjusts weights.
// Spec: "DVA Vendor Evaluation Agent: Spec v0.1" (Google Doc).
//
// The agent turns an approved use case + the client's context + the sourced landscape into a
// shortlist per sourcing lot. Criterion scores are stored, so changing weights re-ranks
// instantly without re-running the agent. Evidence only: never changes a VRI score.
// ============================================================

export const CRITERIA = [
  { id: 'functional', name: 'Functional fit', short: 'Function', weight: 25 },
  { id: 'integration', name: 'Epic and stack fit', short: 'Epic fit', weight: 20 },
  { id: 'proof', name: 'Proof at peer health systems', short: 'Proof', weight: 15 },
  { id: 'timeToValue', name: 'Time to value and implementation load', short: 'Time to value', weight: 15 },
  { id: 'viability', name: 'Vendor viability and direction', short: 'Viability', weight: 10 },
  { id: 'measurement', name: 'Outcome measurement and data rights', short: 'Measurement', weight: 10 },
  { id: 'commercial', name: 'Commercial transparency', short: 'Commercial', weight: 5 },
] as const;

export type CriterionId = (typeof CRITERIA)[number]['id'];
export type Weights = Record<CriterionId, number>;
export const DEFAULT_WEIGHTS: Weights = Object.fromEntries(CRITERIA.map((c) => [c.id, c.weight])) as Weights;
export const RUBRIC_VERSION = 'vendor-eval-v1';

export interface CriterionScore {
  score: number; // 1–5
  rationale: string;
  evidence: string[]; // URLs
  verify: boolean; // evidence missing: treat as a risk to verify
}

export interface EvaluatedVendor {
  vendor: string;
  status: 'shortlist' | 'nearMiss' | 'evaluated';
  rank: number;
  weightedScore: number;
  scores: Record<CriterionId, CriterionScore>;
  whyShortlisted?: string;
  keyRisk?: string;
  verifyInDemo?: string[];
  rfpQuestions?: string[];
  whyNot?: string;
}

export interface SourcingLot {
  id: string;
  name: string;
  gap: string;
  why: string;
  evaluated: EvaluatedVendor[];
}

export interface VendorEvaluation {
  useCaseId: string;
  asOf: string;
  rubricVersion: string;
  recommendation: { deliveryPath: 'buy' | 'build' | 'combine'; summary: string };
  epicBaseline: { covers: string[]; gaps: string[]; sources: { title: string; url: string; retrieved?: string }[] };
  lots: SourcingLot[];
  screenedOut: { vendor: string; reason: string }[];
  assumptions: string[];
  openQuestions: string[];
}

/** Weighted score 0–100 from stored criterion scores and the given weights (normalized). */
export function weightedScore(scores: Record<CriterionId, CriterionScore>, weights: Weights): number {
  const total = CRITERIA.reduce((s, c) => s + Math.max(0, weights[c.id]), 0) || 1;
  const raw = CRITERIA.reduce((s, c) => s + ((scores[c.id]?.score ?? 0) / 5) * Math.max(0, weights[c.id]), 0);
  return Math.round((raw / total) * 1000) / 10;
}

/** How many vendors the agent shortlisted in this lot: kept constant when weights change. */
export function shortlistSize(lot: SourcingLot): number {
  return Math.max(1, lot.evaluated.filter((v) => v.status === 'shortlist').length);
}

/**
 * Re-rank a lot under client weights. Ties: proof, then integration (rubric rule).
 * Shortlist = top N where N is the agent's shortlist size; the next up to 3 are near misses.
 */
export function rerankLot(lot: SourcingLot, weights: Weights): (EvaluatedVendor & { liveScore: number; liveStatus: EvaluatedVendor['status']; liveRank: number })[] {
  const n = shortlistSize(lot);
  const scored = lot.evaluated.map((v) => ({ ...v, liveScore: weightedScore(v.scores, weights) }));
  scored.sort((a, b) =>
    b.liveScore - a.liveScore ||
    (b.scores.proof?.score ?? 0) - (a.scores.proof?.score ?? 0) ||
    (b.scores.integration?.score ?? 0) - (a.scores.integration?.score ?? 0));
  return scored.map((v, i) => ({
    ...v,
    liveRank: i + 1,
    liveStatus: i < n ? 'shortlist' : i < n + 3 ? 'nearMiss' : 'evaluated',
  }));
}

export function isDefaultWeights(w: Weights): boolean {
  return CRITERIA.every((c) => w[c.id] === DEFAULT_WEIGHTS[c.id]);
}
